import { ReactComponent as CopyIcon } from '@basqet-dashboard/common/assets/icons/copy-icon.svg';
import PropTypes from 'prop-types';
import React from 'react';

import { EM_DASH } from './transaction-detail-utils';

// Grey placeholder used everywhere a value is genuinely absent.
const MutedValue = ({ children }) => (
  <span className="transaction-detail__value--muted">{children}</span>
);

MutedValue.propTypes = { children: PropTypes.node };

const EmptyValue = () => <MutedValue>Not available</MutedValue>;

// Used where a row has no value to report at all, rather than a missing one.
const Dash = () => <MutedValue>{EM_DASH}</MutedValue>;

const CopyValue = ({ value }) => {
  if (!value) return <EmptyValue />;
  const copyValue = () => navigator.clipboard?.writeText(String(value));

  return (
    <span className="transaction-detail__copy-value">
      <span>{value}</span>
      <button type="button" onClick={copyValue} aria-label={`Copy ${value}`}>
        <CopyIcon />
      </button>
    </span>
  );
};

CopyValue.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

const StatusBadge = ({ label, tone = 'neutral', title }) => (
  <span className={`transaction-status transaction-status--${tone}`} title={title}>
    {label}
  </span>
);

StatusBadge.propTypes = {
  label: PropTypes.node.isRequired,
  tone: PropTypes.string,
  title: PropTypes.string
};

const DetailCard = ({ title, badge, children, testId }) => (
  <section className="transaction-detail__card" data-testid={testId}>
    <div className="transaction-detail__card-header">
      <h2>{title}</h2>
      {badge}
    </div>
    {children}
  </section>
);

DetailCard.propTypes = {
  title: PropTypes.string.isRequired,
  badge: PropTypes.node,
  children: PropTypes.node,
  testId: PropTypes.string
};

const DetailRow = ({ label, value, valueClassName = '', copy = false }) => (
  <div className="transaction-detail__row">
    <span>{label}</span>
    <span className={valueClassName}>
      {copy ? <CopyValue value={value} /> : value ?? <EmptyValue />}
    </span>
  </div>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  valueClassName: PropTypes.string,
  copy: PropTypes.bool
};

const DetailMetric = ({ label, value }) => (
  <div className="transaction-detail__metric">
    <span>{label}</span>
    <strong>{value ?? <EmptyValue />}</strong>
  </div>
);

DetailMetric.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

// `SETTLEMENT_FEE` -> `Settlement Fee`. Returns `null` so rows fall back to `EmptyValue`.
const titleCase = (value) =>
  value
    ? String(value)
        .toLowerCase()
        .replace(
          /(^|_)(\w)/g,
          (_, separator, letter) => `${separator ? ' ' : ''}${letter.toUpperCase()}`
        )
    : null;

export {
  CopyValue,
  Dash,
  DetailCard,
  DetailMetric,
  DetailRow,
  EmptyValue,
  MutedValue,
  StatusBadge,
  titleCase
};
